import {
  cashAccounts,
  cashBalances,
  cashEvents,
  cashFlowRules,
  companies,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

const internal = {
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
} as const

export const selectCompanySchema = createSelectSchema(companies)
export const insertCompanySchema = createInsertSchema(companies).omit(internal)
export const updateCompanySchema = insertCompanySchema.partial()
export type Company = z.infer<typeof selectCompanySchema>
export type CreateCompany = z.infer<typeof insertCompanySchema>
export type UpdateCompany = z.infer<typeof updateCompanySchema>

export const selectCashAccountSchema = createSelectSchema(cashAccounts)
export const insertCashAccountSchema =
  createInsertSchema(cashAccounts).omit(internal)
export const updateCashAccountSchema = insertCashAccountSchema.partial()
export const selectCashAccountWithCompanySchema =
  selectCashAccountSchema.extend({
    company: selectCompanySchema.pick({ id: true, name: true }).nullable(),
  })
export type CashAccount = z.infer<typeof selectCashAccountSchema>
export type CashAccountWithCompany = z.infer<
  typeof selectCashAccountWithCompanySchema
>
export type CreateCashAccount = z.infer<typeof insertCashAccountSchema>
export type UpdateCashAccount = z.infer<typeof updateCashAccountSchema>

export const selectCashEventSchema = createSelectSchema(cashEvents)
export const insertCashEventSchema =
  createInsertSchema(cashEvents).omit(internal)
export const updateCashEventSchema = insertCashEventSchema.partial()
export const selectCashEventWithCompanySchema = selectCashEventSchema.extend({
  company: selectCompanySchema.pick({ id: true, name: true }).nullable(),
  rule: z.object({ id: z.number(), name: z.string() }).nullable(),
})
export type CashEvent = z.infer<typeof selectCashEventSchema>
export type CashEventWithCompany = z.infer<
  typeof selectCashEventWithCompanySchema
>
export type CreateCashEvent = z.infer<typeof insertCashEventSchema>
export type UpdateCashEvent = z.infer<typeof updateCashEventSchema>

export const selectCashBalanceSchema = createSelectSchema(cashBalances)
export const insertCashBalanceSchema =
  createInsertSchema(cashBalances).omit(internal)
export const updateCashBalanceSchema = insertCashBalanceSchema.partial()
export type CashBalance = z.infer<typeof selectCashBalanceSchema>
export type CreateCashBalance = z.infer<typeof insertCashBalanceSchema>
export type UpdateCashBalance = z.infer<typeof updateCashBalanceSchema>

export const selectCashFlowRuleSchema = createSelectSchema(cashFlowRules)
// A rule never nets to zero and never describes money already in the bank, so
// `transfer`, `actual` and `committed` are not offered.
export const insertCashFlowRuleSchema = createInsertSchema(cashFlowRules, {
  direction: z.enum(['in', 'out']),
  certainty: z.enum(['expected', 'forecast']),
  dayRule: z.number().int().min(1).max(31),
}).omit(internal)
export const updateCashFlowRuleSchema = insertCashFlowRuleSchema.partial()
export type CashFlowRule = z.infer<typeof selectCashFlowRuleSchema>
export type CreateCashFlowRule = z.infer<typeof insertCashFlowRuleSchema>
export type UpdateCashFlowRule = z.infer<typeof updateCashFlowRuleSchema>

export const cashDirections = ['in', 'out', 'transfer'] as const
export type CashDirection = (typeof cashDirections)[number]

export const cashCertaintyLevels = [
  'actual',
  'committed',
  'expected',
  'forecast',
] as const
export type CashCertainty = (typeof cashCertaintyLevels)[number]

export const cashFlowDailyInputSchema = z
  .object({
    // Include events up to and including this certainty level
    upTo: z.enum(cashCertaintyLevels).default('expected'),
    // Slices the returned points; the running balance is still computed from
    // the opening balance date (YYYY-MM-DD)
    from: z.string().optional(),
    // Last day of the horizon (YYYY-MM-DD); defaults to the end of next year
    to: z.string().optional(),
  })
  .refine((input) => !input.from || !input.to || input.from <= input.to, {
    message: 'from must be on or before to',
    path: ['from'],
  })

export const cashFlowDailyPointSchema = z.object({
  date: z.string(),
  balance: z.number(),
  in: z.number(),
  out: z.number(),
})

export const cashFlowDailySeriesSchema = z.object({
  companyId: z.number().nullable(), // null = group
  name: z.string(),
  isGroupParent: z.boolean().nullable(), // null = group
  points: z.array(cashFlowDailyPointSchema),
  minBalance: z.number(),
  minDate: z.string(),
})

// Where an account's curve starts: its latest observed balance, or the
// account's own opening figure when nothing has been observed yet.
export const cashFlowDailyAccountSchema = z.object({
  accountId: z.number(),
  companyId: z.number(),
  name: z.string(),
  kind: z.enum(['bank', 'stripe']),
  balance: z.number(),
  date: z.string(),
  source: z.string(), // 'manual' | 'fennoa:ledger' | 'bank' | 'opening'
})

export const cashFlowDailyResponseSchema = z.object({
  openingDate: z.string(),
  accounts: z.array(cashFlowDailyAccountSchema),
  series: z.array(cashFlowDailySeriesSchema),
})

export type CashFlowDailyPoint = z.infer<typeof cashFlowDailyPointSchema>
export type CashFlowDailyAccount = z.infer<typeof cashFlowDailyAccountSchema>
export type CashFlowDailySeries = z.infer<typeof cashFlowDailySeriesSchema>
export type CashFlowDailyResponse = z.infer<typeof cashFlowDailyResponseSchema>

// The ledger view of the same events the curve is drawn from: one row per day,
// broken down by category. The balance is taken from the daily series rather
// than recomputed, so the chart and the ledger cannot disagree about a day.
export const cashEventsGroupedInputSchema = z
  .object({
    // Absent means no date bound at all, so a cleared filter sums exactly the
    // rows the list then shows rather than a window of its own invention.
    from: z.string().optional(),
    to: z.string().optional(),
    upTo: z.enum(cashCertaintyLevels).default('forecast'),
    // A number is one company. `null` is the consolidated group, matching
    // `cashFlowDailySeriesSchema.companyId`, and drops both legs of every
    // intercompany event the way the curve does. `undefined` is no company
    // filter: every row of every company, intercompany legs included, which is
    // what a register shows and what a consolidated balance cannot be built
    // from.
    companyId: z.number().nullish(),
    // The same filters the flat list shows, so a visible control never sits
    // dead in grouped mode.
    certainty: z.enum(cashCertaintyLevels).optional(),
    direction: z.enum(cashDirections).optional(),
    source: z.string().optional(),
    search: z.string().optional(),
  })
  .refine((input) => !input.from || !input.to || input.from <= input.to, {
    message: 'from must be on or before to',
    path: ['from'],
  })

export const cashEventTotalsSchema = z.object({
  count: z.number(),
  in: z.number(),
  out: z.number(),
  // Transfers move money between our own accounts, so they net to zero and are
  // reported beside the net rather than inside it.
  transfer: z.number(),
  net: z.number(),
})

export const cashEventCategoryGroupSchema = cashEventTotalsSchema.extend({
  category: z.string(),
  events: z.array(selectCashEventWithCompanySchema),
})

export const cashEventDayGroupSchema = cashEventTotalsSchema.extend({
  date: z.string(),
  // Absent for a day the running balance does not reach: the curve starts at
  // the account's latest observed balance, so earlier days have no point.
  balance: z.number().nullable(),
  categories: z.array(cashEventCategoryGroupSchema),
})

export const cashEventsGroupedResponseSchema = z.object({
  // Null whenever the window has no single company to carry a balance: with no
  // company filter the same money appears on both sides of an intercompany
  // pair, so a running total would count it twice.
  openingBalance: z.number().nullable(),
  closingBalance: z.number().nullable(),
  days: z.array(cashEventDayGroupSchema),
  totals: cashEventTotalsSchema,
})

export type CashEventsGroupedInput = z.infer<
  typeof cashEventsGroupedInputSchema
>
// What a caller passes, before the schema fills its defaults in.
export type CashEventsGroupedArgs = z.input<typeof cashEventsGroupedInputSchema>
export type CashEventTotals = z.infer<typeof cashEventTotalsSchema>
export type CashEventCategoryGroup = z.infer<
  typeof cashEventCategoryGroupSchema
>
export type CashEventDayGroup = z.infer<typeof cashEventDayGroupSchema>
export type CashEventsGroupedResponse = z.infer<
  typeof cashEventsGroupedResponseSchema
>

// Where a company's rows live in Fennoa's web app; null when it has no Fennoa
// of its own or the credential is missing.
export const fennoaBaseUrlSchema = z.object({
  companyId: z.number(),
  baseUrl: z.string().nullable(),
})

export type FennoaBaseUrl = z.infer<typeof fennoaBaseUrlSchema>

// One assumption the forecast runs on, for the report that carries the model
// out of the ERP. Reference only: nothing is computed from these.
export const forecastDriverSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.union([z.number(), z.string()]),
  unit: z.string(),
  source: z.string(),
})

export type ForecastDriver = z.infer<typeof forecastDriverSchema>
